import React, { useMemo, useState } from 'react';

import sortSvg from '@assets/img/icons/sort.svg';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { RoutePlanAddItemDialog } from '@components/admin/widgets/RoutePlanAddItemDialog';
import { Button } from '@components/Button';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { CheckboxInput } from '@components/Forms/CheckboxInput';
import { Table, cellFactory } from '@components/Table/Table';
import { agencyStrings } from '@data/agency';
import { getCityLabelByName, podCityNames, polCityNames } from '@data/cities';
import { routePlanServiceNameStrings, routePlanStrings } from '@data/routePlan';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { useAgencyList } from '@hooks/queries/dashboard/useAgencyList';
import { useRoutePlanList } from '@hooks/queries/dashboard/useRoutePlanList';
import { useUser } from '@hooks/queries/useUser';
import { useSearch } from '@hooks/useSearch';
import { routePlanQueryForm, serviceName } from '@models/RoutePlan';
import { meta } from '@pages/admin/widgets/RoutePlanManagement.pagemeta';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { toggleArray } from '@utils/types';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { omitBy, sortBy } from 'lodash-es';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: meta.title,
    addItem: '新規追加',
    exportCSV: 'Export CSV',
    from: 'から',
    to: 'まで',
    search: '絞り込み',
    exportSubject: '本船スケジュール',
    details: '詳細',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '1rem',
  },
  formContainer: {
    ...common.flexColumn,
    marginLeft: 20,
    marginTop: 28,
    marginBottom: 28,
    rowGap: '1.5rem',
  },
  searchContainer: {
    ...common.middleText,
    ...common.textGray600,
    display: 'grid',
    columnGap: 20,
    rowGap: 18,
    gridTemplateColumns: 'auto 1fr',
    padding: 12,
    marginBottom: 6,
    '& *[role="label"]': {
      ...common.textAlignRight,
    },
    '& *[role="input"]': {
      ...common.flexInline,
      columnGap: '1rem',
      flexWrap: 'wrap',
    },
  },
  input: {
    width: 140,
  },
  joinLabel: {
    ...common.middleText,
    color: theme.palette.grey[700],
    margin: '0 0.5rem',
    paddingTop: 6,
  },
  clickable: {
    pointerEvents: 'all',
  },
}));

const validateSearch = createValidate(routePlanQueryForm);

export const RoutePlanManagement: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch, setSearch, clearSearch } = useSearch(routePlanQueryForm);

  const [routePlanAddItemOpen, setRoutePlanAddItemOpen] = useState(false);

  const [checklist, setChecklist] = useState<number[]>([]);

  const [exportCsvOpen, setExportCsvOpen] = useState(false);

  const query = {
    page: 1,
    page_size: 50,
    ...search,
  };

  const routePlanListResponse = useRoutePlanList(search);
  const routePlanList = routePlanListResponse.data ?? [];

  const user = useUser();
  const isAdmin = user.data?.role === 'admin';

  const renderSortButton = (orderName: string) => (
    <img
      src={sortSvg}
      alt="Sort"
      className={classes.baseButton}
      style={{ position: 'relative', top: 3 }}
      onClick={() => {
        updateSearch({
          ordering: toggleArray(query.ordering ?? [], orderName),
        });
      }}
    />
  );

  const agencyList = useAgencyList();

  const sortedAgencyList = useMemo(() => {
    return agencyList.data ? sortBy(agencyList.data, (d) => d.id) : [];
  }, [agencyList.data]);

  const allChecked = useMemo(
    () =>
      routePlanList.length > 0 &&
      routePlanList.map((v) => v.id).every((id) => checklist.includes(id)),
    [checklist, routePlanList],
  );

  const renderSearchContainer = () => (
    <Formik
      validate={validateSearch}
      initialValues={{
        agency_id: query.agency_id ?? [],
        ordering: query.ordering ?? [],
        pod: query.pod ?? [],
        pol: query.pol ?? [],
        service_name: query.service_name ?? [],
      }}
      enableReinitialize
      onSubmit={(values) =>
        setSearch(omitBy(values, (v) => (Array.isArray(v) ? v.length === 0 : false)))
      }
    >
      <Form className={classes.searchContainer}>
        <div role="label">{routePlanStrings.serviceName}</div>

        <div role="input">
          {serviceName.types.map(({ value }) => (
            <CheckboxInput
              key={value}
              name="service_name"
              value={value}
              label={routePlanServiceNameStrings[value]}
              classes={{ container: classes.textGray600Important }}
            />
          ))}
        </div>
        <div role="label">{agencyStrings.agency}</div>

        <div role="input">
          {sortedAgencyList.map((agency) => (
            <CheckboxInput
              key={agency.id}
              name="agency_id"
              value={agency.id}
              label={agency.name_en_short}
              classes={{ container: classes.textGray600Important }}
            />
          ))}
        </div>

        <div role="label">{vesselScheduleStrings.pol}</div>
        <div role="input">
          {polCityNames.map((item) => (
            <CheckboxInput
              key={item}
              name="pol"
              value={item}
              label={getCityLabelByName(item)}
              classes={{ container: classes.textGray600Important }}
            />
          ))}
        </div>

        <div role="label">{vesselScheduleStrings.pod}</div>
        <div role="input">
          {podCityNames.map((item) => (
            <CheckboxInput
              key={item}
              name="pod"
              value={item}
              label={getCityLabelByName(item)}
              classes={{ container: classes.textGray600Important }}
            />
          ))}
        </div>

        <div style={{ gridColumn: 'span 2', columnGap: '0.5rem' }} className={classes.flexInline}>
          <Button color="primary" shape="round" text={strings.search} type="submit" size="small" />
          <Button
            color="primary"
            shape="round"
            text={formStrings.reset}
            type="button"
            size="small"
            onClick={() => clearSearch()}
          />
        </div>
      </Form>
    </Formik>
  );

  const renderTable = () => {
    const checkableHeaderColumn = cellFactory.createCheckable(allChecked, () => {
      if (allChecked) {
        setChecklist([]);
      } else {
        setChecklist(routePlanList.map((item) => item.id));
      }
    });

    return (
      <Table columnFixedUntil={6} fixedWidths={[56, 60, 150, 170, 120, 70]}>
        <Table.Head>
          <Table.Row
            columns={[
              checkableHeaderColumn,
              cellFactory.create('NO.'),
              cellFactory.create(
                <>
                  {routePlanStrings.serviceName} {renderSortButton('service_name')}
                </>,
              ),
              cellFactory.create(
                <>
                  {routePlanStrings.routeName} {renderSortButton('name')}
                </>,
              ),
              cellFactory.create(
                <>
                  {agencyStrings.agency} {renderSortButton('agency')}
                </>,
              ),
              cellFactory.create(''),
              cellFactory.create(agencyStrings.receivingWarehouse),
              cellFactory.create(agencyStrings.exportWarehouse),
              cellFactory.create(
                <>
                  {vesselScheduleStrings.pol} {renderSortButton('pol')}
                </>,
              ),
              cellFactory.create(
                <>
                  {vesselScheduleStrings.pod} {renderSortButton('pod')}
                </>,
              ),
            ]}
          />
        </Table.Head>
        <Table.Body>
          {routePlanList.map((item, i) => (
            <Table.Row
              disabled={item.disabled}
              key={item.id}
              columns={[
                cellFactory.createCheckable(checklist.includes(item.id), () =>
                  checklist.includes(item.id)
                    ? setChecklist(checklist.filter((id) => id !== item.id))
                    : setChecklist([...checklist, item.id]),
                ),
                cellFactory.create(i + 1),
                cellFactory.create(routePlanServiceNameStrings[item.service_name]),
                cellFactory.create(item.name),
                cellFactory.create(item.agency.name_en_short),
                cellFactory.create(
                  <Link
                    className={clsx(classes.link, classes.baseButton, classes.clickable)}
                    to={`${url('/admin/widgets/RoutePlanDetail')}?id=${item.id}`}
                  >
                    {strings.details}
                  </Link>,
                ),
                cellFactory.create(item.receiving_warehouses.map((r) => r.name).join('/')),
                cellFactory.create(item.export_warehouse?.name ?? '-'),
                cellFactory.create(getCityLabelByName(item.pol)),
                cellFactory.create(getCityLabelByName(item.pod)),
              ]}
            />
          ))}
        </Table.Body>
      </Table>
    );
  };

  return (
    <Layout
      title={strings.title!}
      loading={routePlanListResponse.isLoading || agencyList.isLoading}
      errors={[routePlanListResponse.error, agencyList.error]}
    >
      <RoutePlanAddItemDialog
        open={routePlanAddItemOpen}
        agencyList={agencyList.data ?? []}
        onClose={() => setRoutePlanAddItemOpen(false)}
      />

      <div className={classes.container}>
        <ExportCSVDialog
          open={exportCsvOpen}
          onClose={() => setExportCsvOpen(false)}
          subject={routePlanStrings.route}
          baseUrl="/dashboard/route-plan/"
          selectedIds={checklist}
          query={search}
        />

        <SectionTitle
          actions={
            <div className={classes.buttonContainer}>
              {isAdmin && (
                <Button
                  onClick={() => setRoutePlanAddItemOpen(true)}
                  color="primary"
                  text={strings.addItem}
                  shape="round"
                />
              )}
              <Button
                onClick={() => setExportCsvOpen(true)}
                color="primary"
                text={strings.exportCSV}
                shape="round"
              />
            </div>
          }
        >
          {strings.title}
        </SectionTitle>
        {renderSearchContainer()}
        {renderTable()}
      </div>
    </Layout>
  );
};
